import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Image,
  Platform,
} from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { Ionicons } from '@expo/vector-icons';
import {
  Header,
  ScreenLayout,
  StepperReturnBanner,
  colors,
  spacing,
  typography,
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { xTwitterAuthService, XTwitterAuthTokens } from '../services/x-twitter-auth.service';
import { decodeJWT } from '../utils/token-utils';
import { tokenManager } from '../services/token-manager';
import { useThemedStyles } from '../hooks/useThemedStyles';

WebBrowser.maybeCompleteAuthSession();

type SetupStep = 'connect' | 'oauth' | 'confirmation' | 'success';

interface ConnectedAccount {
  xUserId: string;
  xUsername: string;
  displayName: string;
  profileImageUrl?: string;
  webhookSubscribed: boolean;
  status: string;
  tokenExpired: boolean;
}

export const XTwitterSetupScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, setActiveTab, activeTab, routeParams } = useAppContext();
  // Embedded inside the channel onboarding stepper, the caller passes where "done" should go
  // next (the next step, not always the inbox) via routeParams.onCompleteScreen/onCompleteParams.
  const onCompleteScreen = (routeParams as any)?.onCompleteScreen || 'chat';
  const onCompleteParams = (routeParams as any)?.onCompleteParams;
  const isEmbeddedInStepper = !!(routeParams as any)?.onCompleteScreen;
  const { user, currentBusiness } = useAppStore();

  const [step, setStep] = useState<SetupStep>('connect');
  const [isLoading, setIsLoading] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [pendingTokens, setPendingTokens] = useState<XTwitterAuthTokens | null>(null);
  const [maxAccounts, setMaxAccounts] = useState(1);
  const [showLeftPane, setShowLeftPane] = useState(Platform.OS === 'web');

  useEffect(() => {
    setActiveTab('home');
    loadConnectedAccounts();
  }, []);

  const loadConnectedAccounts = useCallback(async () => {
    try {
      const [accountsRes, limitsRes] = await Promise.all([
        apiService.getXTwitterAccounts(),
        apiService.getXTwitterLimits(),
      ]);

      if (accountsRes?.data) {
        setConnectedAccounts(accountsRes.data);
      }
      if (limitsRes?.data?.maxAccounts) {
        setMaxAccounts(limitsRes.data.maxAccounts);
      }

      // Also read from JWT for instant display
      const token = await tokenManager.getToken();
      if (token) {
        const decoded = decodeJWT(token);
        const subscriptionMax = decoded?.subscription?.services?.x_twitter?.number_of_x_accounts;
        if (subscriptionMax) setMaxAccounts(subscriptionMax);
      }
    } catch (err) {
      console.warn('[XTwitterSetup] loadConnectedAccounts error:', err);
    }
  }, []);

  // ─── Step 1: Initiate OAuth ───────────────────────────────────────────────

  const handleConnectPress = async () => {
    if (connectedAccounts.length >= maxAccounts) {
      Alert.alert(
        'Account Limit Reached',
        `Your plan allows ${maxAccounts} X account${maxAccounts > 1 ? 's' : ''}. Upgrade your subscription to connect more.`,
        [{ text: 'OK' }]
      );
      return;
    }

    if (!xTwitterAuthService.isConfigured()) {
      const hasClientId = !!xTwitterAuthService.getClientId();
      const redirectUri = xTwitterAuthService.getRedirectUri();
      Alert.alert(
        'Configuration Missing',
        `X Twitter OAuth is not configured correctly.\n\nClient ID: ${hasClientId ? 'Configured' : 'Missing'}\nRedirect URI: ${redirectUri || 'Missing'}`,
        [{ text: 'OK' }]
      );
      return;
    }

    setIsLoading(true);
    setStep('oauth');

    try {
      const pkce = await xTwitterAuthService.generatePkce();
      if (Platform.OS === 'web') {
        pkce.state = `web_${pkce.state}`;
      } else {
        // dx_ tells the API to redirect back on the dislix:// scheme
        pkce.state = `dx_${pkce.state}`;
      }
      const authUrl = xTwitterAuthService.buildAuthUrl(pkce);
      let code: string | null = null;
      let state: string | null = null;
      let error: string | null = null;

      if (Platform.OS === 'web') {
        const popup = window.open(authUrl, 'x-twitter-oauth', 'width=520,height=720');
        if (!popup) {
          throw new Error('Popup blocked. Please allow popups and try again.');
        }

        const result = await new Promise<{ code: string | null; state: string | null; error: string | null }>((resolve, reject) => {
          const timeout = window.setTimeout(() => {
            cleanup();
            reject(new Error('OAuth timed out. Please try again.'));
          }, 120000);

          const closedInterval = window.setInterval(() => {
            if (popup.closed) {
              cleanup();
              reject(new Error('OAuth popup closed before completion.'));
            }
          }, 500);

          const onMessage = (event: MessageEvent) => {
            const data = event.data;
            if (!data || data.source !== 'sendifier-x-oauth') return;
            cleanup();
            resolve({ code: data.code ?? null, state: data.state ?? null, error: data.error ?? null });
          };

          const cleanup = () => {
            window.clearTimeout(timeout);
            window.clearInterval(closedInterval);
            window.removeEventListener('message', onMessage);
            try { popup.close(); } catch {}
          };

          window.addEventListener('message', onMessage);
        });

        code = result.code;
        state = result.state;
        error = result.error;
      } else {
        const redirectUri = 'dislix://x-twitter-callback';
        const result = await WebBrowser.openAuthSessionAsync(authUrl, redirectUri);
        console.log('[XTwitterSetup] Auth session result:', result);

        if (result.type === 'cancel' || result.type === 'dismiss') {
          showNotification('warning', 'Cancelled', 'X authorization was cancelled.');
          setStep('connect');
          setIsLoading(false);
          return;
        }

        if (result.type !== 'success' || !result.url) {
          throw new Error(`OAuth flow failed: ${result.type}`);
        }

        const parsed = xTwitterAuthService.parseCallbackUrl(result.url);
        code = parsed.code;
        state = parsed.state;
        error = parsed.error;
      }

      if (error) throw new Error(`X OAuth error: ${error}`);
      if (state !== pkce.state) throw new Error('State mismatch — possible CSRF');
      if (!code) throw new Error('No authorization code received');

      // Exchange code for tokens via Sendifier backend
      const apiBaseUrl = apiService.getBaseURL();
      const tokens = await xTwitterAuthService.exchangeCode(
        code,
        pkce.codeVerifier,
        apiBaseUrl
      );

      setPendingTokens(tokens);
      setStep('confirmation');
    } catch (err: any) {
      console.error('[XTwitterSetup] OAuth error:', err);
      showNotification('error', 'Connection Failed', err.message ?? 'Failed to connect X account');
      setStep('connect');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Step 2: Confirm and save ─────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!pendingTokens) return;
    setIsLoading(true);

    try {
      // POST /api/x-twitter/accounts — stores tokens + subscribes webhook
      await apiService.connectXTwitterAccount({
        xUserId: pendingTokens.user.id,
        xUsername: pendingTokens.user.username,
        displayName: pendingTokens.user.name,
        profileImageUrl: pendingTokens.user.profile_image_url,
        oauth2AccessToken: pendingTokens.access_token,
        oauth2RefreshToken: pendingTokens.refresh_token,
        oauth2ExpiresAt: pendingTokens.expires_at,
      });

      // Backfill existing DM conversations
      try {
        const importResult = await apiService.importXTwitterConversations(pendingTokens.user.id);
        console.log('[XTwitterSetup] Imported conversations:', importResult?.data);
      } catch (importErr) {
        console.warn('[XTwitterSetup] Conversation import failed (non-fatal):', importErr);
      }

      // Apply default channel settings
      if (currentBusiness?._id) {
        try {
          await apiService.upsertChannelSettings({
            businessId: currentBusiness._id,
            channel: 'x_twitter',
            channelIdentifier: pendingTokens.user.id,
          });
        } catch (settingsErr) {
          console.warn('[XTwitterSetup] Channel settings upsert failed (non-fatal):', settingsErr);
        }
      }

      await loadConnectedAccounts();
      setStep('success');
      showNotification('success', 'Connected!', `@${pendingTokens.user.username} is now connected to your inbox.`);
    } catch (err: any) {
      console.error('[XTwitterSetup] handleConfirm error:', err);
      showNotification('error', 'Error', err.message ?? 'Failed to save X account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async (xUserId: string, xUsername: string) => {
    Alert.alert(
      'Disconnect Account',
      `Disconnect @${xUsername} from DISLIX? Existing conversations will be preserved.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Disconnect',
          style: 'destructive',
          onPress: async () => {
            try {
              await apiService.disconnectXTwitterAccount(xUserId);
              await loadConnectedAccounts();
              showNotification('success', 'Disconnected', `@${xUsername} has been disconnected.`);
            } catch (err: any) {
              showNotification('error', 'Error', err.message ?? 'Failed to disconnect account');
            }
          }
        }
      ]
    );
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const handleOpenDeveloperPlayground = async () => {
    try {
      const { setPendingSettingsItem } = await import('../utils/tempStorage');
      await setPendingSettingsItem('developer');
      navigateTo('settings');
    } catch (error) {
      console.error('[XTwitterSetup] Failed to navigate to developer settings:', error);
      showNotification('error', 'Navigation Error', 'Unable to open Developer Access right now.');
    }
  };

  // ─── Renders ──────────────────────────────────────────────────────────────

  const renderConnectStep = () => (
    <ScrollView contentContainerStyle={styles.stepContainer}>
      {/* X Logo + Title */}
      <View style={styles.heroContainer}>
        <View style={styles.xLogoContainer}>
          <Ionicons name="logo-twitter" size={48} color="#FFFFFF" />
        </View>
        <Text style={styles.heroTitle}>X (Twitter)</Text>
        <Text style={styles.heroSubtitle}>
          Connect your X account to manage Direct Messages and @mentions directly from your DISLIX inbox.
        </Text>
      </View>

      {/* Feature list */}
      <View style={styles.featuresCard}>
        {[
          { icon: 'chatbubble-ellipses', text: 'Manage Direct Messages' },
          { icon: 'at', text: 'Respond to @mentions' },
          { icon: 'flash', text: 'Auto-reply and Quick Replies' },
          { icon: 'people', text: 'Unified contact profiles' },
          { icon: 'notifications', text: 'Real-time message notifications' },
        ].map(({ icon, text }) => (
          <View key={text} style={styles.featureRow}>
            <Ionicons name={icon as any} size={18} color={colors.primary ?? '#1DA1F2'} />
            <Text style={styles.featureText}>{text}</Text>
          </View>
        ))}
      </View>

      {/* X Profile Completion Card */}
      <View style={styles.profileCompletionCard}>
        <View style={styles.profileCompletionHeader}>
          <Text style={styles.profileCompletionTitle}>X Profile Completion</Text>
          <Text style={styles.profileCompletionPercent}>
            {connectedAccounts.length > 0 ? '100%' : '0%'}
          </Text>
        </View>

        <View style={styles.completionRow}>
          <Ionicons
            name={connectedAccounts.length > 0 ? 'checkmark-circle' : 'ellipse-outline'}
            size={16}
            color={connectedAccounts.length > 0 ? '#22C55E' : '#9CA3AF'}
          />
          <Text style={styles.completionText}>Connect at least one X account</Text>
        </View>
        <View style={styles.completionRow}>
          <Ionicons
            name={connectedAccounts.some(acc => !acc.tokenExpired) ? 'checkmark-circle' : 'ellipse-outline'}
            size={16}
            color={connectedAccounts.some(acc => !acc.tokenExpired) ? '#22C55E' : '#9CA3AF'}
          />
          <Text style={styles.completionText}>Keep token active (not expired)</Text>
        </View>

        <TouchableOpacity
          style={styles.developerButton}
          onPress={handleOpenDeveloperPlayground}
          activeOpacity={0.8}
        >
          <Ionicons name="code-slash-outline" size={16} color={colors.primary ?? '#1DA1F2'} />
          <Text style={styles.developerButtonText}>Open Developer Playground</Text>
        </TouchableOpacity>
      </View>

      {/* Connected accounts list */}
      {connectedAccounts.length > 0 && (
        <View style={styles.connectedSection}>
          <Text style={styles.sectionTitle}>Connected Accounts ({connectedAccounts.length}/{maxAccounts})</Text>
          {connectedAccounts.map(acc => (
            <View key={acc.xUserId} style={styles.accountCard}>
              <View style={styles.accountInfo}>
                <View style={styles.accountAvatar}>
                  <Ionicons name="logo-twitter" size={20} color="#000" />
                </View>
                <View>
                  <Text style={styles.accountName}>{acc.displayName}</Text>
                  <Text style={styles.accountUsername}>@{acc.xUsername}</Text>
                  {acc.tokenExpired && (
                    <Text style={styles.expiredBadge}>Token expired — reconnect</Text>
                  )}
                </View>
              </View>
              <View style={styles.accountActions}>
                <View style={[styles.statusDot, { backgroundColor: acc.status === 'active' && !acc.tokenExpired ? '#22C55E' : '#EF4444' }]} />
                <TouchableOpacity onPress={() => handleDisconnect(acc.xUserId, acc.xUsername)}>
                  <Ionicons name="trash-outline" size={18} color="#EF4444" />
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Connect button */}
      <TouchableOpacity
        style={[styles.connectButton, isLoading && styles.buttonDisabled]}
        onPress={handleConnectPress}
        disabled={isLoading}
      >
        {isLoading ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <>
            <Ionicons name="logo-twitter" size={20} color="#FFFFFF" />
            <Text style={styles.connectButtonText}>
              {connectedAccounts.length > 0 ? 'Connect Another Account' : 'Connect X Account'}
            </Text>
          </>
        )}
      </TouchableOpacity>
    </ScrollView>
  );

  const renderOAuthStep = () => (
    <View style={styles.stepContainer}>
      <ActivityIndicator size="large" color="#000000" />
      <Text style={styles.oauthText}>Opening X authorization…</Text>
      <Text style={styles.oauthSubtext}>
        A browser window will open. Sign in to X and approve the permissions to continue.
      </Text>
    </View>
  );

  const renderConfirmationStep = () => {
    if (!pendingTokens) return null;
    return (
      <ScrollView contentContainerStyle={styles.stepContainer}>
        <View style={styles.confirmCard}>
          <View style={styles.xLogoSmall}>
            <Ionicons name="logo-twitter" size={28} color="#FFFFFF" />
          </View>
          <Text style={styles.confirmTitle}>Account Ready to Connect</Text>
          <View style={styles.profileRow}>
            {pendingTokens.user.profile_image_url ? (
              <Image source={{ uri: pendingTokens.user.profile_image_url }} style={styles.profileImage} />
            ) : (
              <View style={styles.profileImagePlaceholder}>
                <Ionicons name="person" size={24} color="#6B7280" />
              </View>
            )}
            <View style={styles.profileInfo}>
              <Text style={styles.profileName}>{pendingTokens.user.name}</Text>
              <Text style={styles.profileUsername}>@{pendingTokens.user.username}</Text>
            </View>
            <Ionicons name="checkmark-circle" size={24} color="#22C55E" />
          </View>

          <View style={styles.permissionsList}>
            <Text style={styles.permissionsTitle}>Permissions granted:</Text>
            {['Read Direct Messages', 'Send Direct Messages', 'Read @mentions', 'Read profile info'].map(p => (
              <View key={p} style={styles.permissionRow}>
                <Ionicons name="checkmark" size={14} color="#22C55E" />
                <Text style={styles.permissionText}>{p}</Text>
              </View>
            ))}
          </View>
        </View>

        <TouchableOpacity
          style={[styles.connectButton, isLoading && styles.buttonDisabled]}
          onPress={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.connectButtonText}>Complete Connection</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity style={styles.cancelButton} onPress={() => { setPendingTokens(null); setStep('connect'); }}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
      </ScrollView>
    );
  };

  const renderSuccessStep = () => (
    <View style={[styles.stepContainer, styles.centered]}>
      <View style={styles.successIcon}>
        <Ionicons name="checkmark-circle" size={64} color="#22C55E" />
      </View>
      <Text style={styles.successTitle}>X Account Connected!</Text>
      <Text style={styles.successSubtitle}>
        {pendingTokens ? `@${pendingTokens.user.username} is now` : 'Your account is'} live in your DISLIX inbox. DMs and @mentions will appear in real time.
      </Text>
      <TouchableOpacity style={styles.connectButton} onPress={() => navigateTo(onCompleteScreen, undefined, onCompleteParams)}>
        <Text style={styles.connectButtonText}>{isEmbeddedInStepper ? 'Continue' : 'Go to Inbox'}</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.secondaryActionButton} onPress={handleOpenDeveloperPlayground}>
        <Ionicons name="code-slash-outline" size={16} color={colors.primary ?? '#1DA1F2'} />
        <Text style={styles.secondaryActionButtonText}>Open Developer Playground</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.cancelButton} onPress={() => { setPendingTokens(null); setStep('connect'); }}>
        <Text style={styles.cancelButtonText}>Connect Another Account</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={false}
          showMessages={false}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => setShowLeftPane(false)}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
    >
      <StepperReturnBanner />
      <View style={styles.content}>
        {step === 'connect' && renderConnectStep()}
        {step === 'oauth' && renderOAuthStep()}
        {step === 'confirmation' && renderConfirmationStep()}
        {step === 'success' && renderSuccessStep()}
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  content: { flex: 1 },
  stepContainer: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  heroContainer: { alignItems: 'center', marginBottom: spacing.xl },
  xLogoContainer: {
    width: 80, height: 80, borderRadius: 40,
    backgroundColor: '#000000',
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.md,
  },
  heroTitle: { ...typography.h1, marginBottom: spacing.sm },
  heroSubtitle: { ...typography.body, textAlign: 'center', color: colors.textSecondary ?? '#6B7280' },

  featuresCard: {
    backgroundColor: colors.background.secondary, borderRadius: 12, padding: spacing.md,
    marginBottom: spacing.xl,
  },
  featureRow: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm },
  featureText: { ...typography.body, marginLeft: spacing.sm },

  profileCompletionCard: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.xl,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  profileCompletionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  profileCompletionTitle: {
    ...typography.bodyBold,
    color: '#111827',
  },
  profileCompletionPercent: {
    ...typography.caption,
    color: '#6B7280',
  },
  completionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
    gap: 6,
  },
  completionText: {
    ...typography.caption,
    color: '#374151',
  },
  developerButton: {
    marginTop: spacing.sm,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#F8FAFC',
  },
  developerButtonText: {
    ...typography.caption,
    color: colors.primary ?? '#1DA1F2',
  },

  connectedSection: { marginBottom: spacing.xl },
  sectionTitle: { ...typography.h3, marginBottom: spacing.sm },
  accountCard: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: colors.background.surface, borderRadius: 10, padding: spacing.md,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: '#E5E7EB',
  },
  accountInfo: { flexDirection: 'row', alignItems: 'center' },
  accountAvatar: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: colors.background.secondary,
    justifyContent: 'center', alignItems: 'center', marginRight: spacing.sm,
  },
  accountName: { ...typography.bodyBold },
  accountUsername: { ...typography.caption, color: colors.textSecondary ?? '#6B7280' },
  expiredBadge: { ...typography.caption, color: '#EF4444' },
  accountActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  statusDot: { width: 8, height: 8, borderRadius: 4 },

  connectButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#000000', borderRadius: 10, padding: spacing.md,
    gap: spacing.sm, marginBottom: spacing.sm,
  },
  connectButtonText: { color: '#FFFFFF', ...typography.bodyBold },
  secondaryActionButton: {
    marginBottom: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 10,
    padding: spacing.md,
    gap: 6,
    backgroundColor: '#F8FAFC',
  },
  secondaryActionButtonText: {
    ...typography.body,
    color: colors.primary ?? '#1DA1F2',
  },
  buttonDisabled: { opacity: 0.6 },
  cancelButton: { alignItems: 'center', padding: spacing.sm },
  cancelButtonText: { ...typography.body, color: colors.textSecondary ?? '#6B7280' },

  oauthText: { ...typography.h3, textAlign: 'center', marginTop: spacing.xl },
  oauthSubtext: { ...typography.body, textAlign: 'center', color: colors.textSecondary ?? '#6B7280', marginTop: spacing.sm },

  confirmCard: {
    backgroundColor: colors.background.surface, borderRadius: 12, padding: spacing.lg,
    marginBottom: spacing.xl, borderWidth: 1, borderColor: '#E5E7EB',
  },
  xLogoSmall: {
    width: 48, height: 48, borderRadius: 24, backgroundColor: '#000000',
    justifyContent: 'center', alignItems: 'center', alignSelf: 'center', marginBottom: spacing.md,
  },
  confirmTitle: { ...typography.h2, textAlign: 'center', marginBottom: spacing.lg },
  profileRow: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.lg },
  profileImage: { width: 48, height: 48, borderRadius: 24, marginRight: spacing.sm },
  profileImagePlaceholder: {
    width: 48, height: 48, borderRadius: 24, backgroundColor: colors.background.secondary,
    justifyContent: 'center', alignItems: 'center', marginRight: spacing.sm,
  },
  profileInfo: { flex: 1 },
  profileName: { ...typography.bodyBold },
  profileUsername: { ...typography.caption, color: colors.textSecondary ?? '#6B7280' },
  permissionsList: { borderTopWidth: 1, borderTopColor: '#E5E7EB', paddingTop: spacing.md },
  permissionsTitle: { ...typography.caption, color: colors.textSecondary ?? '#6B7280', marginBottom: spacing.sm },
  permissionRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  permissionText: { ...typography.caption, marginLeft: 6 },

  successIcon: { marginBottom: spacing.lg },
  successTitle: { ...typography.h1, textAlign: 'center', marginBottom: spacing.sm },
  successSubtitle: {
    ...typography.body, textAlign: 'center',
    color: colors.textSecondary ?? '#6B7280', marginBottom: spacing.xl,
  },
});
